import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import { useAuth } from '@/contexts/AuthContext';
import { useOperations } from '@/hooks/useOperations';
import type { PurchaseDetail } from '@/hooks/usePurchases';
import {
  purchaseStatusLabel,
  purchaseLifecycleLabel,
  purchasePaymentState,
  purchaseBalance,
  saleMoney,
} from '@klothiq/domain';
import PageHeader from '@/components/common/PageHeader';
import PurchaseLifecycleActions from '@/components/purchases/PurchaseLifecycleActions';
import PurchasePayments from '@/components/purchases/PurchasePayments';
import PurchaseOrderPreview from '@/components/purchases/PurchaseOrderPreview';
import { exportPurchasesPdf } from '@/utils/pdfTemplates';
export default function PurchaseRecordDetail({ purchase: p }: { purchase: PurchaseDetail }) {
  const { can } = useAuth();
  const ops = useOperations();
  const [preview, setPreview] = useState(false);
  const balance = purchaseBalance(p);
  const active = !p.is_deleted && !p.is_voided;
  return (
    <div className="space-y-5">
      <PageHeader
        title={p.purchase_number}
        description={
          p.workflow_mode === 'record_only'
            ? 'Purchase record · stock is managed separately'
            : 'Legacy stock order · original receiving history retained'
        }
        backTo="/purchases"
        actions={
          <div className="flex flex-wrap gap-2">
            {active && !p.archived_at && can('purchases.edit') && (
              <Link
                to={`/purchases/${p.id}/edit`}
                className="min-h-11 rounded-xl border border-border px-4 py-3 text-sm"
              >
                Edit purchase
              </Link>
            )}
            <PurchaseLifecycleActions purchase={p} />
          </div>
        }
      />
      <section className="glass-card space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap gap-2">
          {purchaseLifecycleLabel(p) && (
            <span className="rounded-full bg-amber-500/10 px-3 py-1 text-sm">
              {purchaseLifecycleLabel(p)}
            </span>
          )}
          <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
            {purchaseStatusLabel(p)}
          </span>
          <span className="rounded-full border border-border px-3 py-1 text-sm">
            {purchasePaymentState(p).replaceAll('_', ' ')}
          </span>
        </div>
        <h2 className="text-xl font-semibold">{p.suppliers?.name || 'Supplier'}</h2>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Purchase date', p.purchase_date],
            [
              'Delivery date',
              p.delivered_on || (p.status === 'received' ? 'Not recorded (legacy)' : 'Pending'),
            ],
            ['Invoice / reference', p.supplier_invoice_no || '—'],
            ['Entry', p.details_mode === 'amount_only' ? 'Amount only' : 'Itemized'],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-muted-foreground">{k}</dt>
              <dd className="mt-1 break-words text-sm">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-3">
          {[
            ['Total', saleMoney(p.total_amount)],
            ['Recorded payments', saleMoney(p.paid_amount)],
            ['Supplier balance', balance === null ? 'Not tracked' : saleMoney(balance)],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">{k}</p>
              <p className="mt-1 text-xl font-semibold">{v}</p>
            </div>
          ))}
        </div>
        {balance === null && (
          <p className="text-sm text-muted-foreground">
            No payment is recorded or asserted as an unpaid debt. Record a payment only if it is not
            already entered in Accounts.
          </p>
        )}
        {p.notes && <p className="whitespace-pre-wrap text-sm">{p.notes}</p>}
        {p.is_deleted && (
          <p role="status" className="rounded-xl bg-amber-500/10 p-3 text-sm">
            In Recycle Bin: {p.deletion_reason}. Restore before editing. No records were permanently
            erased.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPreview(true)}
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
          >
            Preview / Print
          </button>
          <button
            onClick={() =>
              exportPurchasesPdf([{ ...p, supplier_name: p.suppliers?.name || 'Supplier' }])
            }
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
          >
            Download PDF
          </button>
        </div>
      </section>
      {!!p.purchase_items.length && (
        <section className="glass-card space-y-3 p-4 sm:p-6">
          <h2 className="font-semibold">Item details</h2>
          <p className="text-xs text-muted-foreground">
            {p.workflow_mode === 'record_only'
              ? 'Informational only · no stock posting'
              : 'Legacy item details · receiving history retained'}
          </p>
          {p.purchase_items.map((i) => (
            <article key={i.id} className="rounded-xl border border-border p-3">
              <h3 className="break-words font-medium">
                {i.description ||
                  i.product_variants?.products?.name ||
                  i.product_variants?.sku ||
                  'Saved item'}
              </h3>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                {[
                  ['Quantity', String(i.quantity)],
                  ['Price', saleMoney(i.unit_cost)],
                  ['Discount', saleMoney(i.discount)],
                  ['Total', saleMoney(i.line_total)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </section>
      )}
      <PurchasePayments purchase={p} enabled={active && ops.data?.modules.purchases === 1} />
      {preview && <PurchaseOrderPreview purchase={p} onClose={() => setPreview(false)} />}
    </div>
  );
}
